import { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type {
  ScheduledEvent,
  YoutubeChannelVideo,
  YoutubeSettingsPublic,
} from "@tih/shared";
import {
  ApiError,
  adminFetchEvents,
  adminFetchYoutubeVideos,
  adminSyncYoutubeChannel,
  adminUpdateYoutubeVideo,
} from "../../api/client";

function fmt(n: number): string {
  return n.toLocaleString();
}

function formatWhen(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

type EditDraft = {
  category: string;
  description: string;
  tags: string;
  eventId: string;
  publishedOnSite: boolean;
};

type PublishedFilter = "all" | "yes" | "no";
type EventFilter = "all" | "linked" | "none";

function draftFromVideo(v: YoutubeChannelVideo): EditDraft {
  return {
    category: v.category ?? "",
    description: v.description ?? "",
    tags: v.tags.join(", "),
    eventId: v.eventId != null ? String(v.eventId) : "",
    publishedOnSite: v.publishedOnSite,
  };
}

export function AdminYoutubeLibraryPage() {
  const [videos, setVideos] = useState<YoutubeChannelVideo[]>([]);
  const [settings, setSettings] = useState<YoutubeSettingsPublic | null>(null);
  const [events, setEvents] = useState<ScheduledEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncBusy, setSyncBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saveBusy, setSaveBusy] = useState(false);

  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [publishedFilter, setPublishedFilter] = useState<PublishedFilter>("all");
  const [eventFilter, setEventFilter] = useState<EventFilter>("all");

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [data, ev] = await Promise.all([
        adminFetchYoutubeVideos(),
        adminFetchEvents().catch(() => [] as ScheduledEvent[]),
      ]);
      setVideos(data.videos);
      setSettings(data.settings);
      setEvents(ev);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load video library");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const categories = useMemo(() => {
    const set = new Set<string>();
    for (const v of videos) {
      if (v.category?.trim()) set.add(v.category.trim());
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [videos]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return videos.filter((v) => {
      if (categoryFilter && (v.category ?? "") !== categoryFilter) return false;
      if (publishedFilter === "yes" && !v.publishedOnSite) return false;
      if (publishedFilter === "no" && v.publishedOnSite) return false;
      if (eventFilter === "linked" && v.eventId == null) return false;
      if (eventFilter === "none" && v.eventId != null) return false;
      if (q) {
        const hay = `${v.title} ${v.videoId} ${v.category ?? ""} ${v.tags.join(" ")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [videos, search, categoryFilter, publishedFilter, eventFilter]);

  async function syncChannel() {
    setSyncBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const data = await adminSyncYoutubeChannel();
      setVideos(data.videos);
      setSettings(data.settings);
      setSuccess(
        `Synced ${data.synced} videos from ${data.channelTitle}. Site metadata (category, description, tags, event, published) was preserved.`,
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Sync failed");
    } finally {
      setSyncBusy(false);
    }
  }

  function toggleEdit(v: YoutubeChannelVideo) {
    if (expandedId === v.videoId) {
      setExpandedId(null);
      setDraft(null);
      return;
    }
    setExpandedId(v.videoId);
    setDraft(draftFromVideo(v));
  }

  async function saveMeta(videoId: string) {
    if (!draft) return;
    setSaveBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const tags = draft.tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
      const eventIdRaw = draft.eventId.trim();
      const eventId =
        eventIdRaw === ""
          ? null
          : Number.isInteger(Number(eventIdRaw)) && Number(eventIdRaw) > 0
            ? Number(eventIdRaw)
            : null;
      const updated = await adminUpdateYoutubeVideo(videoId, {
        category: draft.category.trim() || null,
        description: draft.description.trim() || null,
        tags,
        eventId,
        publishedOnSite: draft.publishedOnSite,
      });
      setVideos((prev) => prev.map((row) => (row.videoId === videoId ? updated : row)));
      setSuccess(`Saved metadata for “${updated.title}”.`);
      setExpandedId(null);
      setDraft(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save video metadata");
    } finally {
      setSaveBusy(false);
    }
  }

  return (
    <div>
      <header className="admin-page-header">
        <div>
          <h1 className="admin-page-title">YouTube library</h1>
          <p className="admin-page-sub">
            Channel uploads with view counts stored in the database. Edit site metadata for the
            public Videos gallery; sync never wipes those fields.
          </p>
        </div>
        <div className="admin-page-actions">
          <Link to="/admin/settings/youtube" className="btn btn-ghost">
            Settings
          </Link>
          <Link to="/videos" className="btn btn-ghost">
            Public gallery
          </Link>
          <button
            type="button"
            className="btn btn-primary"
            disabled={syncBusy || !settings?.configured}
            onClick={() => void syncChannel()}
          >
            {syncBusy ? "Syncing…" : "Sync channel"}
          </button>
        </div>
      </header>

      {settings && (
        <section className="admin-card" style={{ marginBottom: 16 }}>
          <p className="admin-card-sub" style={{ margin: 0 }}>
            {settings.channelId ? (
              <>
                Channel: <strong>{settings.channelTitle ?? settings.channelId}</strong>
                {" · "}
                {settings.videoCount} videos in library
                {settings.lastSyncAt && <> · Last sync {formatWhen(settings.lastSyncAt)}</>}
              </>
            ) : (
              <>
                Set a channel id or @handle in{" "}
                <Link to="/admin/settings/youtube">Settings → YouTube</Link>, then sync.
              </>
            )}
          </p>
        </section>
      )}

      {error && <div className="form-error">{error}</div>}
      {success && (
        <div className="form-success" style={{ padding: 12, marginBottom: 16 }}>
          {success}
        </div>
      )}

      {!loading && videos.length > 0 && (
        <section
          className="admin-card"
          style={{
            marginBottom: 16,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: 12,
            alignItems: "end",
          }}
        >
          <label>
            <span>Search</span>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Title, id, tags…"
            />
          </label>
          <label>
            <span>Category</span>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="">All categories</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Published on site</span>
            <select
              value={publishedFilter}
              onChange={(e) => setPublishedFilter(e.target.value as PublishedFilter)}
            >
              <option value="all">All</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </label>
          <label>
            <span>Event</span>
            <select
              value={eventFilter}
              onChange={(e) => setEventFilter(e.target.value as EventFilter)}
            >
              <option value="all">All</option>
              <option value="linked">Has event</option>
              <option value="none">No event</option>
            </select>
          </label>
          <p className="admin-card-sub" style={{ margin: 0, gridColumn: "1 / -1" }}>
            Showing {filtered.length} of {videos.length}
          </p>
        </section>
      )}

      {loading ? (
        <div style={{ opacity: 0.5 }}>Loading…</div>
      ) : videos.length === 0 ? (
        <section className="admin-card">
          <p className="admin-card-sub">
            No videos yet. Configure your API key and channel, then click <strong>Sync channel</strong>.
          </p>
        </section>
      ) : filtered.length === 0 ? (
        <section className="admin-card">
          <p className="admin-card-sub">No videos match these filters.</p>
        </section>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Video</th>
                <th>Category</th>
                <th>On site</th>
                <th>Published</th>
                <th>Views</th>
                <th>Likes</th>
                <th>Comments</th>
                <th>Synced</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((v) => (
                <Fragment key={v.id}>
                  <tr>
                    <td>
                      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                        {v.thumbnailUrl && (
                          <img
                            src={v.thumbnailUrl}
                            alt=""
                            width={80}
                            height={45}
                            style={{ borderRadius: 4, objectFit: "cover" }}
                          />
                        )}
                        <div>
                          <strong style={{ display: "block" }}>{v.title}</strong>
                          <div className="admin-card-foot" style={{ marginTop: 4 }}>
                            <Link to={`/videos/view/${encodeURIComponent(v.videoId)}`}>
                              View on site
                            </Link>
                            {" · "}
                            <a
                              href={`https://www.youtube.com/watch?v=${encodeURIComponent(v.videoId)}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              YouTube
                            </a>
                            {v.liveBroadcast && (
                              <>
                                {" · "}
                                {v.concurrentViewers != null
                                  ? `${fmt(v.concurrentViewers)} watching live`
                                  : "Live / was live"}
                              </>
                            )}
                            {v.eventId != null && (
                              <>
                                {" · "}
                                Event #{v.eventId}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>{v.category ?? "—"}</td>
                    <td>{v.publishedOnSite ? "Yes" : "No"}</td>
                    <td>{formatWhen(v.publishedAt)}</td>
                    <td>{fmt(v.viewCount)}</td>
                    <td>{fmt(v.likeCount)}</td>
                    <td>{fmt(v.commentCount)}</td>
                    <td>{formatWhen(v.syncedAt)}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => toggleEdit(v)}
                      >
                        {expandedId === v.videoId ? "Close" : "Edit"}
                      </button>
                    </td>
                  </tr>
                  {expandedId === v.videoId && draft && (
                    <tr>
                      <td colSpan={9}>
                        <div
                          className="admin-card"
                          style={{ margin: "8px 0 16px", padding: 16 }}
                          role="region"
                          aria-label={`Edit ${v.title}`}
                        >
                          <h3 style={{ margin: "0 0 12px", fontSize: 16 }}>
                            Edit site metadata
                          </h3>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: 12,
                            }}
                          >
                            <label>
                              <span>Category</span>
                              <input
                                type="text"
                                maxLength={80}
                                value={draft.category}
                                onChange={(e) =>
                                  setDraft({ ...draft, category: e.target.value })
                                }
                                disabled={saveBusy}
                                placeholder="e.g. Worship night"
                              />
                            </label>
                            <label>
                              <span>Event (optional)</span>
                              <select
                                value={draft.eventId}
                                onChange={(e) =>
                                  setDraft({ ...draft, eventId: e.target.value })
                                }
                                disabled={saveBusy}
                              >
                                <option value="">— None —</option>
                                {events.map((ev) => (
                                  <option key={ev.id} value={String(ev.id)}>
                                    #{ev.id}{" "}
                                    {ev.title ??
                                      new Date(ev.startsAt).toLocaleDateString()}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <label style={{ gridColumn: "1 / -1" }}>
                              <span>Description</span>
                              <textarea
                                rows={3}
                                value={draft.description}
                                onChange={(e) =>
                                  setDraft({ ...draft, description: e.target.value })
                                }
                                disabled={saveBusy}
                              />
                            </label>
                            <label style={{ gridColumn: "1 / -1" }}>
                              <span>Tags (comma-separated)</span>
                              <input
                                type="text"
                                value={draft.tags}
                                onChange={(e) =>
                                  setDraft({ ...draft, tags: e.target.value })
                                }
                                disabled={saveBusy}
                                placeholder="worship, overnight, kampala"
                              />
                            </label>
                            <label
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={draft.publishedOnSite}
                                onChange={(e) =>
                                  setDraft({
                                    ...draft,
                                    publishedOnSite: e.target.checked,
                                  })
                                }
                                disabled={saveBusy}
                              />
                              <span>Published on site</span>
                            </label>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              gap: 8,
                              marginTop: 12,
                              justifyContent: "flex-end",
                              flexWrap: "wrap",
                            }}
                          >
                            <Link
                              to={`/videos/view/${encodeURIComponent(v.videoId)}`}
                              className="btn btn-ghost"
                            >
                              View on site
                            </Link>
                            <button
                              type="button"
                              className="btn btn-ghost"
                              disabled={saveBusy}
                              onClick={() => {
                                setExpandedId(null);
                                setDraft(null);
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="btn btn-primary"
                              disabled={saveBusy}
                              onClick={() => void saveMeta(v.videoId)}
                            >
                              {saveBusy ? "Saving…" : "Save metadata"}
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
